/**
 * Encoding & byte utilities for Battini Vault crypto.
 *
 * These helpers are intentionally dependency-free and work in browsers,
 * Cloudflare Workers, and Node. They never touch secret material beyond
 * passing it through — no logging, no persistence.
 *
 * `Bytes` is an ArrayBuffer-backed Uint8Array, which is what the Web Crypto
 * `BufferSource` parameters require. All byte producers here return `Bytes`.
 */

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/** ArrayBuffer-backed byte array (assignable to Web Crypto `BufferSource`). */
export type Bytes = Uint8Array<ArrayBuffer>;

/** UTF-8 encode a string to bytes (ArrayBuffer-backed). */
export function toBytes(str: string): Bytes {
  // Copy into a fresh ArrayBuffer-backed view so the type is Bytes (not ArrayBufferLike).
  return new Uint8Array(textEncoder.encode(str));
}

/** UTF-8 decode bytes to a string. */
export function fromBytes(bytes: Bytes): string {
  return textDecoder.decode(bytes);
}

/** Base64 (standard, with padding). */
export function toBase64(bytes: Bytes): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s);
}

/** Base64url (no padding) — compact encoding for tokens / wire transport. */
export function toBase64Url(bytes: Bytes): string {
  return toBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Parse standard OR url-safe base64, with or without padding. */
export function fromBase64(b64: string): Bytes {
  const normalized = b64.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Hex encoding (used for non-secret local digests, e.g. reuse detection). */
export function toHex(bytes: Bytes): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += bytes[i]!.toString(16).padStart(2, '0');
  return s;
}

/**
 * Cryptographically-strong random bytes. Wraps the platform CSPRNG.
 * `Math.random` is never used for security material anywhere in this package.
 */
export function randomBytes(length: number): Bytes {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

/**
 * Pack an IV + ciphertext into a single portable string: `b64(iv || ciphertext)`.
 * The IV is a fixed length per algorithm (12 bytes for AES-GCM), so the reader
 * can split unambiguously.
 */
export function packIvAndCiphertext(iv: Bytes, ciphertext: Bytes): string {
  const combined = new Uint8Array(iv.length + ciphertext.length);
  combined.set(iv, 0);
  combined.set(ciphertext, iv.length);
  return toBase64Url(combined);
}

/** Split a packed `iv||ciphertext` string back into its parts. */
export function unpackIvAndCiphertext(packed: string, ivLength: number): { iv: Bytes; ciphertext: Bytes } {
  const combined = fromBase64(packed);
  const iv = combined.slice(0, ivLength);
  const ciphertext = combined.slice(ivLength);
  return { iv, ciphertext };
}

/** Constant-time comparison of two equal-length byte arrays. */
export function constantTimeEqual(a: Bytes, b: Bytes): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}
